/**
 * 登録画面で扱うエラーマッピング群。
 * UI から切り離してユニットテスト可能にしている。
 */

/**
 * Firebase Auth のエラーコードを localized message に変換する。
 * クライアント側で投げられた FirebaseError と、サーバー側 GraphQL から
 * extensions.providerCode で伝えられた値の両方に使う。
 */
export const toFirebaseAuthMessage = (
  code: string,
  fallback: string,
): string => {
  switch (code) {
    case "auth/email-already-in-use":
    case "auth/email-already-exists":
      return "このメールアドレスは既に登録されています。ログインしてください。"
    case "auth/invalid-email":
      return "メールアドレスの形式が正しくありません。"
    // weak-password は Firebase Client SDK、invalid-password は Admin SDK が
    // 同じ「6 文字未満」相当のケースで投げる。挙動は同じなので同じ案内にする。
    case "auth/weak-password":
    case "auth/invalid-password":
      return "パスワードが弱すぎます。6 文字以上の強力なパスワードを使用してください。"
    case "auth/operation-not-allowed":
      return "メール/パスワードでのサインアップが無効になっています。"
    default:
      return fallback ? `認証エラー: ${fallback}` : `認証エラー: ${code}`
  }
}

type ErrorWithGraphQLErrors = {
  graphQLErrors?: ReadonlyArray<{
    extensions?: { code?: unknown; providerCode?: unknown }
  }>
}

/**
 * ApolloError の `graphQLErrors` 配列を走査して
 * `extensions.code === "AUTH_PROVIDER_ERROR"` のものから providerCode を取り出す。
 * 該当が無ければ null。
 */
export const extractAuthProviderCode = (error: unknown): string | null => {
  if (typeof error !== "object" || error === null) return null
  const gqlErrors = (error as ErrorWithGraphQLErrors).graphQLErrors
  if (!gqlErrors) return null
  for (const gqlError of gqlErrors) {
    const ext = gqlError.extensions
    if (
      ext &&
      ext.code === "AUTH_PROVIDER_ERROR" &&
      typeof ext.providerCode === "string"
    ) {
      return ext.providerCode
    }
  }
  return null
}
